import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Formik } from "formik";
import {
    Autocomplete,
    createTheme,
    Divider,
    FormHelperText,
    IconButton,
    TextField,
    ThemeProvider,
    Typography,
    useTheme,
    Grid,
    Grid2,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    FormControlLabel,
    Switch,
    FormControl,
    FormLabel,
    Slider,
} from "@mui/material";
import * as Yup from "yup";
import { useState } from "react";
import { IoMdClose } from "react-icons/io";
import { useAddKeyRiskMutation } from "store/slices/apis/keyRiskSlice";
import { useNavigate, useParams } from "react-router-dom";
import { RxCross2 } from "react-icons/rx";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useCreateDataAlertMutation } from "store/slices/apis/dataAlertManagementSlice";
import { useGetAllSettingsQuery } from "store/slices/apis/projectSettingsSlice";
import { useGetAllRoleQuery } from "store/slices/apis/rolePackageSlice";
import { ReactComponent as CrossIcon } from "assets/PwC_Funct_Icons_Close_Outline_Black_RGB.svg"
import { ReactComponent as CalendarIcon } from "assets/PwC_Funct_Icons_Calendar_Outline_Black_RGB.svg"

const valueType = [
    { label: "Value", value: "value" },
    { label: "Range", value: "range" },
];

const intervals = [
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
    { label: "When Change Occurs", value: "change" },
];

const repeats = [
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
];

const weekdays = [
    { label: "Monday", value: "monday" },
    { label: "Tuesday", value: "tuesday" },
    { label: "Wednesday", value: "wednesday" },
    { label: "Thursday", value: "thursday" },
    { label: "Friday", value: "friday" },
    { label: "Saturday", value: "saturday" },
    { label: "Sunday", value: "sunday" },
];

const months = [
    { label: "January", value: "january" },
    { label: "February", value: "february" },
    { label: "March", value: "march" },
    { label: "April", value: "april" },
    { label: "May", value: "may" },
    { label: "June", value: "june" },
    { label: "July", value: "july" },
    { label: "August", value: "august" },
    { label: "September", value: "september" },
    { label: "October", value: "october" },
    { label: "November", value: "november" },
    { label: "December", value: "december" },
];

export default function AddDataAlert(props) {
    const { open, cancelIssue, openNotification, refetch } = props;
    const newtheme = useTheme();
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [createDataAlert, { isLoading }] = useCreateDataAlertMutation();

    const { data: allSettings } = useGetAllSettingsQuery({
        ProjectId: projectId,
    });

    const { data: allRole } = useGetAllRoleQuery({
        projectId: projectId,
    });

    const metricsItems = allSettings?.data?.find((setting) => setting.name === "Alert Metrics")?.items || [];

    const dataMetric = metricsItems.map((item) => ({
        label: item.value,
        value: item.value.toLowerCase().replace(/\s+/g, "_"),
    }));

    const roleData = allRole?.data?.map((item) => ({
        label: item.displayName,
        //value: item.displayName.toLowerCase().replace(/\s+/g, "_"),
        value: item.id,
    })) || [];

    const [formData, setFormData] = useState({
        alert_name: "",
        data_metric: "",
        value_type: "",
        value: 0,
        range: "",
        minValue: "",
        maxValue: "",
        interval: "",
        repeat: "",
        weekday: "",
        month: "",
        day: "",
        start_date: "",
        end_date: "",
        status: "",
        recipient: "",
    });

    const theme = createTheme({
        components: {
            MuiTextField: {
                styleOverrides: {
                    root: {
                        border: "2px solid #e6e6e6",
                        "& .MuiFilledInput-input": {
                            backgroundColor: "#fff",
                            color: "#252525",
                            fontSize: "16px",
                        },
                        "& .MuiFilledInput-root::before": {
                            borderBottom: "none",
                        },
                        "& .MuiFilledInput-root::after": {
                            borderBottom: `2px solid ${newtheme?.customSettings?.themeColor}`,
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                            color: "#696969",
                            fontSize: "14px",
                        },
                        "& .MuiFormLabel-root": {
                            fontSize: "15px",
                        },
                        "& .MuiFilledInput-root.Mui-disabled:before": {
                            borderBottom: "none",
                        },
                        "& .MuiFilledInput-root": {
                            backgroundColor: "#fff",
                        },
                        "& .MuiFilledInput-input": {
                            backgroundColor: "none",
                            fontSize: "14px",
                        },
                        "& .MuiInputLabel-root.Mui-disabled": {
                            color: "#696969",
                        },
                        "& .MuiFilledInput-root.Mui-disabled": {
                            backgroundColor: "#fff",
                            color: "inherit",
                        },
                        "& .MuiFilledInput-input.Mui-disabled": {
                            WebkitTextFillColor: "#696969",
                            opacity: "none",
                        },
                    },
                },
            },
        },
    });

    const handleClose = () => { };

    const validationSchema = Yup.object({
        alert_name: Yup.string().required("Alert Name is required"),
        data_metric: Yup.string().required("Data Metric is required"),
        value_type: Yup.string().required("Value Type is required"),

        value: Yup.mixed().when("value_type", {
            is: (val) => val === "Value",
            then: () =>
                Yup.number()
                    .typeError("Value is required")
                    .required("Value is required")
                    .min(0, "Value cannot be negative"),
            otherwise: () => Yup.mixed().notRequired(),
        }),

        range: Yup.array().when("value_type", {
            is: (val) => val === "Range",
            then: () =>
                Yup.array()
                    .of(Yup.number().required("Both min and max are required"))
                    .length(2, "Range must have min and max")
                    .required("Range is required"),
            otherwise: () => Yup.array().notRequired(),
        }),

        interval: Yup.string().required("Interval is required"),

        // 1. Daily → repeat required
        // 2. Weekly → repeat + weekday required
        // 3. Monthly → month + day required
        repeat: Yup.number()
            .typeError("Repeat must be a number")
            .when("interval", {
                is: (val) => val === "Daily" || val === "Weekly",
                then: (schema) =>
                    schema
                        .required("Repeat is required")
                        .min(1, "Repeat must be at least 1"),
                otherwise: (schema) => schema.notRequired(),
            }),

        weekday: Yup.string().when("interval", {
            is: (val) => val === "Weekly",
            then: () => Yup.string().required("Weekday is required"),
            otherwise: () => Yup.string().notRequired(),
        }),

        month: Yup.string().when("interval", {
            is: (val) => val === "Monthly",
            then: () => Yup.string().required("Month is required"),
            otherwise: () => Yup.string().notRequired(),
        }),

        day: Yup.number().when("interval", {
            is: (val) => val === "Monthly",
            then: () =>
                Yup.number()
                    .typeError("Day must be a number")
                    .required("Day of Month is required")
                    .min(1, "Day must be at least 1")
                    .max(31, "Day can't exceed 31"),
            otherwise: () => Yup.number().notRequired(),
        }),

        start_date: Yup.string().required("Start Date is required"),

        recipient: Yup.array().of(Yup.string()).min(1, "Recipient is required").required("Recipient is required"),

        status: Yup.boolean()
            .oneOf([true, false], "Status is required")
            .required("Status is required"),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        const [minValue = null, maxValue = null] = Array.isArray(values.range) ? values.range : [];

        let reqBody = {
            projectId: projectId,
            alertName: values?.alert_name,
            dataMetric: values?.data_metric,
            valueType: values?.value_type,
            value: values?.value,
            //range: values?.range,
            minValue,
            maxValue,
            interval: values?.interval,
            //repeat: 8,
            repeat: values?.interval === 'Daily' || values?.interval === 'Weekly' ? values?.repeat : '',
            day: values?.interval === 'Weekly' ? values?.weekday : '',
            //month: values?.interval === 'Monthly' ? values?.month : '',
            date: values?.interval === 'Monthly' ? values?.day : 0,
            roles: values?.recipient,

            status: values?.status === true ? "Active" : "Inactive",
            startDate:
                values?.start_date && dayjs(values.start_date).isValid()
                    ? dayjs(values.start_date).format("YYYY-MM-DD") + "T00:00:00.000Z"
                    : null,
            endDate:
                values?.end_date && dayjs(values.end_date).isValid()
                    ? dayjs(values.end_date).format("YYYY-MM-DD") + "T00:00:00.000Z"
                    : null,
        };

        try {
            const response = await createDataAlert({ body: reqBody }).unwrap();
            if (response?.status == "success") {
                openNotification("Data Alert created successfully", "success");
                resetForm();
                setSubmitting(false);
                cancelIssue();
                // refetch();
            } else {
                openNotification("Error in data alert creation");
            }
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const CustomCalendarIcon = (props) => (
        <CalendarIcon {...props} style={{ width: 26, height: 26, fill: "#696969 ", stroke: "#696969", strokeWidth: 4 }} />
    );

    return (
        <React.Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={"md"}
                open={open}
                onClose={handleClose}
                sx={{
                    "& .MuiDialog-paper": {
                        overflowY: "inherit",
                    },
                }}
            >
                <DialogTitle sx={{ fontSize: "20px", color: "#696969" }}>
                    Add New Data Alert
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={cancelIssue}
                    sx={(theme) => ({
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    {/* <IoMdClose style={{ fontSize: "20px" }} /> */}
                    <CrossIcon style={{ fontSize: "20px", width: "20px", height: "20px", fill: "#808080 " }} />
                </IconButton>
                <Divider sx={{ w: 1 }} />
                <DialogContent>
                    <ThemeProvider theme={theme}>
                        <Box sx={{ mt: 1 }}>
                            <Formik
                                initialValues={formData}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                                enableReinitialize
                            >
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleChange,
                                    handleBlur,
                                    handleSubmit,
                                    isSubmitting,
                                    setFieldValue,
                                    setTouched,
                                }) => (
                                    <form onSubmit={handleSubmit}>

                                        <Grid2 container spacing={2}>

                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    label="Alert Name"
                                                    name="alert_name"
                                                    fullWidth
                                                    multiline
                                                    value={values?.alert_name}
                                                    onChange={handleChange}
                                                    onBlur={() =>
                                                        setTouched({ ...touched, alert_name: true })
                                                    }
                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                    variant="filled"
                                                />
                                                {touched?.alert_name &&
                                                    errors?.alert_name && (
                                                        <FormHelperText
                                                            sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                        >{`${errors?.alert_name}`}</FormHelperText>
                                                    )}
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <Autocomplete
                                                    options={dataMetric}
                                                    getOptionLabel={(option) => option?.label}
                                                    value={
                                                        dataMetric.find(
                                                            (option) => option.label === values?.data_metric
                                                        ) || null
                                                    }
                                                    onChange={(_, newValue) =>
                                                        setFieldValue(
                                                            "data_metric",
                                                            newValue ? newValue.label : ""
                                                        )
                                                    }
                                                    onBlur={() =>
                                                        setTouched({ ...touched, data_metric: true })
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Data Metric"
                                                            name="data_metric"
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            variant="filled"
                                                        />
                                                    )}
                                                    ListboxProps={{
                                                        sx: { fontSize: "13px" },
                                                    }}
                                                />
                                                {touched?.data_metric && errors?.data_metric && (
                                                    <FormHelperText
                                                        sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                    >{`${errors?.data_metric}`}</FormHelperText>
                                                )}
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <Autocomplete
                                                    options={valueType}
                                                    getOptionLabel={(option) => option?.label}
                                                    value={
                                                        valueType.find(
                                                            (option) => option.label === values?.value_type
                                                        ) || null
                                                    }
                                                    onChange={(_, newValue) =>
                                                        setFieldValue(
                                                            "value_type",
                                                            newValue ? newValue.label : ""
                                                        )
                                                    }
                                                    onBlur={() =>
                                                        setTouched({ ...touched, value_type: true })
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Value Type"
                                                            name="value_type"
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            variant="filled"
                                                        />
                                                    )}
                                                    ListboxProps={{
                                                        sx: { fontSize: "13px" },
                                                    }}
                                                />
                                                {touched?.value_type && errors?.value_type && (
                                                    <FormHelperText
                                                        sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                    >{`${errors?.value_type}`}</FormHelperText>
                                                )}
                                            </Grid2>

                                            {values?.value_type === "Value" && (
                                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        label="Value"
                                                        name="value"
                                                        type="number"
                                                        inputProps={{
                                                            inputMode: 'decimal',
                                                            step: 'any',
                                                            min: 0,
                                                            pattern: "[0-9]*[.]?[0-9]*",
                                                        }}
                                                        value={values?.value}
                                                        onChange={(e) => {
                                                            const val = parseFloat(e.target.value);
                                                            const safeValue = val < 0 ? 0 : val;
                                                            handleChange({
                                                                target: {
                                                                    name: "value",
                                                                    value: safeValue,
                                                                },
                                                            });
                                                        }}
                                                        onBlur={() =>
                                                            setTouched({ ...touched, value: true })
                                                        }
                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                        variant="filled"
                                                        fullWidth
                                                        sx={{
                                                            '& input[type=number]::-webkit-outer-spin-button': {
                                                                display: 'none',
                                                            },
                                                            '& input[type=number]::-webkit-inner-spin-button': {
                                                                display: 'none',
                                                            },
                                                            '& input[type=number]': {
                                                                MozAppearance: 'textfield',
                                                            },
                                                        }}
                                                    />
                                                    {touched?.value && errors?.value && (
                                                        <FormHelperText sx={{ color: "#C52A1A", fontSize: "12px" }}>
                                                            {errors?.value}
                                                        </FormHelperText>
                                                    )}
                                                </Grid2>
                                            )}

                                            {values?.value_type === "Range" && (
                                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                    <FormControl sx={{ width: { xs: '95%', sm: '400px' } }} variant="filled">
                                                        <FormLabel sx={{ mb: 1 }}>Range (Min - Max)</FormLabel>
                                                        <Slider
                                                            value={values?.range || [-100, 100]}
                                                            onChange={(_, newValue) => setFieldValue("range", newValue)}
                                                            onBlur={() => setTouched({ ...touched, range: true })}
                                                            valueLabelDisplay="auto"
                                                            min={-100}
                                                            max={100}
                                                            step={1}
                                                            sx={{
                                                                ml: 1,
                                                                color: '#1976d2', // base color of the slider
                                                                '& .MuiSlider-thumb': {
                                                                    backgroundColor: '#fff',
                                                                    border: '2px solid #1976d2',
                                                                },
                                                                '& .MuiSlider-track': {
                                                                    backgroundColor: '#1976d2',
                                                                },
                                                                '& .MuiSlider-rail': {
                                                                    backgroundColor: '#b0bec5',
                                                                },
                                                                '& .MuiSlider-valueLabel': {
                                                                    backgroundColor: '#1976d2',
                                                                    color: '#fff',
                                                                },
                                                            }}
                                                        />
                                                        {touched?.range && errors?.range && (
                                                            <FormHelperText sx={{ color: "#C52A1A", fontSize: "12px" }}>
                                                                {errors?.range}
                                                            </FormHelperText>
                                                        )}
                                                    </FormControl>
                                                </Grid2>
                                            )}

                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <Autocomplete
                                                    options={intervals}
                                                    getOptionLabel={(option) => option?.label}
                                                    value={
                                                        intervals.find(
                                                            (option) => option.label === values?.interval
                                                        ) || null
                                                    }
                                                    onChange={(_, newValue) => {
                                                        const selectedInterval = newValue ? newValue.label : "";
                                                        setFieldValue("interval", selectedInterval);

                                                        // Reset 'repeat'
                                                        if (selectedInterval === "When Change Occurs") {
                                                            setFieldValue("repeat", "");
                                                        }
                                                    }}
                                                    onBlur={() =>
                                                        setTouched({ ...touched, interval: true })
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Interval"
                                                            name="interval"
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            variant="filled"
                                                        />
                                                    )}
                                                    ListboxProps={{
                                                        sx: { fontSize: "13px" },
                                                    }}
                                                />
                                                {touched?.interval && errors?.interval && (
                                                    <FormHelperText
                                                        sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                    >{`${errors?.interval}`}</FormHelperText>
                                                )}
                                            </Grid2>

                                            {values?.interval === "Daily" ? (
                                                // Repeat TextField when Interval is 'Daily'
                                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                    <TextField
                                                        label="Reoccur every (Days)"
                                                        name="repeat"
                                                        value={values?.repeat}
                                                        onChange={(e) => setFieldValue("repeat", e.target.value)}
                                                        onBlur={() => setTouched({ ...touched, repeat: true })}
                                                        variant="filled"
                                                        fullWidth
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    {touched?.repeat && errors?.repeat && (
                                                        <FormHelperText
                                                            sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                        >{`${errors?.repeat}`}</FormHelperText>
                                                    )}
                                                </Grid2>
                                            ) :
                                                values?.interval === "Weekly" ? (
                                                    <>
                                                        {/* Repeat TextField when Interval is 'Weekly'*/}
                                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                                            <TextField
                                                                label="Reoccur every (Weeks)"
                                                                name="repeat"
                                                                value={values?.repeat}
                                                                onChange={(e) => setFieldValue("repeat", e.target.value)}
                                                                onBlur={() => setTouched({ ...touched, repeat: true })}
                                                                variant="filled"
                                                                fullWidth
                                                                InputLabelProps={{ shrink: true }}
                                                            //sx={{ mb: 2 }}
                                                            />
                                                            {touched?.repeat && errors?.repeat && (
                                                                <FormHelperText
                                                                    sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                                >{`${errors?.repeat}`}</FormHelperText>
                                                            )}
                                                        </Grid2>

                                                        {/* Weekday Dropdown */}
                                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                                            <Autocomplete
                                                                options={weekdays}
                                                                getOptionLabel={(option) => option?.label}
                                                                value={
                                                                    weekdays.find((opt) => opt.label === values?.weekday) || null
                                                                }
                                                                onChange={(_, newValue) =>
                                                                    setFieldValue("weekday", newValue ? newValue.label : "")
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="On Day"
                                                                        name="weekday"
                                                                        variant="filled"
                                                                        InputLabelProps={{ shrink: true }}
                                                                    />
                                                                )}
                                                                ListboxProps={{
                                                                    sx: { fontSize: "13px" },
                                                                }}
                                                            />
                                                            {touched?.weekday && errors?.weekday && (
                                                                <FormHelperText
                                                                    sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                                >{`${errors?.weekday}`}</FormHelperText>
                                                            )}
                                                        </Grid2>
                                                    </>
                                                ) :
                                                    values?.interval === "Monthly" ? (
                                                        <>
                                                            {/* Month Dropdown */}
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Autocomplete
                                                                    options={months}
                                                                    getOptionLabel={(option) => option.label}
                                                                    value={
                                                                        months.find(
                                                                            (option) => option.label === values?.month
                                                                        ) || null
                                                                    }
                                                                    onChange={(_, newValue) =>
                                                                        setFieldValue("month", newValue ? newValue.label : "")
                                                                    }
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            label="Month"
                                                                            name="month"
                                                                            variant="filled"
                                                                            InputLabelProps={{ shrink: true }}
                                                                        //sx={{ mb: 2 }}
                                                                        />
                                                                    )}
                                                                    ListboxProps={{
                                                                        sx: { fontSize: "13px" },
                                                                    }}
                                                                />
                                                                {touched?.month && errors?.month && (
                                                                    <FormHelperText
                                                                        sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                                    >{`${errors?.month}`}</FormHelperText>
                                                                )}
                                                            </Grid2>

                                                            {/* Day Dropdown (1–31) */}
                                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                                <Autocomplete
                                                                    options={Array.from({ length: 31 }, (_, i) => i + 1).map((day) => ({
                                                                        label: `Day ${day}`,
                                                                        value: day,
                                                                    }))}
                                                                    getOptionLabel={(option) => option.label}
                                                                    value={
                                                                        Array.from({ length: 31 }, (_, i) => i + 1)
                                                                            .map((day) => ({ label: `Day ${day}`, value: day }))
                                                                            .find((opt) => opt.value === values?.day) || null
                                                                    }
                                                                    onChange={(_, newValue) =>
                                                                        setFieldValue("day", newValue ? newValue.value : "")
                                                                    }
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            label="Day of Month"
                                                                            name="day"
                                                                            variant="filled"
                                                                            InputLabelProps={{ shrink: true }}
                                                                        />
                                                                    )}
                                                                    ListboxProps={{
                                                                        sx: { fontSize: "13px" },
                                                                    }}
                                                                />
                                                                {touched?.day && errors?.day && (
                                                                    <FormHelperText
                                                                        sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                                    >{`${errors?.day}`}</FormHelperText>
                                                                )}
                                                            </Grid2>
                                                        </>
                                                    ) : (
                                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                                            <Autocomplete
                                                                options={repeats}
                                                                getOptionLabel={(option) => option?.label}
                                                                value={
                                                                    repeats.find(
                                                                        (option) => option.label === values?.repeat
                                                                    ) || null
                                                                }
                                                                onChange={(_, newValue) =>
                                                                    setFieldValue(
                                                                        "repeat",
                                                                        newValue ? newValue.label : ""
                                                                    )
                                                                }
                                                                //   onBlur={() =>
                                                                //     setTouched({ ...touched, interval: true })
                                                                //   }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Repeat"
                                                                        name="repeat"
                                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                                        variant="filled"
                                                                    />
                                                                )}
                                                                ListboxProps={{
                                                                    sx: { fontSize: "13px" },
                                                                }}
                                                                // Disable 'repeat'
                                                                disabled={values?.interval === 'When Change Occurs'}
                                                            />
                                                        </Grid2>
                                                    )}


                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        label="Start Date"
                                                        value={
                                                            values?.start_date
                                                                ? dayjs(values.start_date)
                                                                : null
                                                        }
                                                        format="DD-MM-YYYY"
                                                        minDate={dayjs()}
                                                        maxDate={values?.end_date ? dayjs(values.end_date) : undefined}
                                                        onChange={(newValue) => {
                                                            handleChange({
                                                                target: {
                                                                    name: "start_date",
                                                                    value: newValue ? newValue : "",
                                                                },
                                                            });
                                                        }}
                                                        slots={{
                                                            openPickerIcon: CustomCalendarIcon,
                                                        }}
                                                        onBlur={() =>
                                                            setTouched({
                                                                ...touched,
                                                                start_date: true,
                                                            })
                                                        }
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                variant: "filled",
                                                                InputLabelProps: {
                                                                    style: { fontSize: 16, fontWeight: 400 },
                                                                    shrink: true,
                                                                },
                                                                inputProps: {
                                                                    style: { height: "20px" },
                                                                },
                                                                placeholder: "DD/MM/YYYY",
                                                                sx: {
                                                                    input: {
                                                                        "&::placeholder": {
                                                                            fontSize: 16,
                                                                            fontWeight: 400,
                                                                        },
                                                                    },
                                                                    "& .MuiFormLabel-asterisk": {
                                                                        color: "red",
                                                                        fontSize: 20,
                                                                    },
                                                                },
                                                                onKeyDown: (e) => e.preventDefault(),
                                                            },
                                                            inputLabel: { shrink: true },
                                                        }}
                                                    />
                                                    {touched?.start_date && errors?.start_date && (
                                                        <FormHelperText
                                                            sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                        >{`${errors?.start_date}`}</FormHelperText>
                                                    )}
                                                </LocalizationProvider>
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DatePicker
                                                        label="End Date"
                                                        value={
                                                            values?.end_date
                                                                ? dayjs(values.end_date)
                                                                : null
                                                        }
                                                        format="DD-MM-YYYY"
                                                        minDate={values?.start_date ? dayjs(values.start_date) : dayjs()}
                                                        onChange={(newValue) => {
                                                            handleChange({
                                                                target: {
                                                                    name: "end_date",
                                                                    value: newValue ? newValue : "",
                                                                },
                                                            });
                                                        }}
                                                        slots={{
                                                            openPickerIcon: CustomCalendarIcon,
                                                        }}
                                                        // onBlur={() =>
                                                        //   setTouched({
                                                        //     ...touched,
                                                        //     risk_reporting_date: true,
                                                        //   })
                                                        // }
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                variant: "filled",
                                                                InputLabelProps: {
                                                                    style: { fontSize: 16, fontWeight: 400 },
                                                                    shrink: true,
                                                                },
                                                                inputProps: {
                                                                    style: { height: "20px" },
                                                                },
                                                                placeholder: "DD/MM/YYYY",
                                                                sx: {
                                                                    input: {
                                                                        "&::placeholder": {
                                                                            fontSize: 16,
                                                                            fontWeight: 400,
                                                                        },
                                                                    },
                                                                    "& .MuiFormLabel-asterisk": {
                                                                        color: "red",
                                                                        fontSize: 20,
                                                                    },
                                                                },
                                                                onKeyDown: (e) => e.preventDefault(),
                                                            },
                                                            inputLabel: { shrink: true },
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <Autocomplete
                                                    multiple
                                                    options={roleData}
                                                    getOptionLabel={(option) => option?.label}
                                                    value={
                                                        roleData?.filter((option) =>
                                                            values?.recipient?.includes(option.value)
                                                        ) || []
                                                    }
                                                    onChange={(_, newValue) =>
                                                        setFieldValue(
                                                            "recipient",
                                                            newValue ? newValue.map((item) => item.value) : []
                                                        )
                                                    }
                                                    onBlur={() =>
                                                        setTouched({ ...touched, recipient: true })
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Recipient"
                                                            name="recipient"
                                                            slotProps={{ inputLabel: { shrink: true } }}
                                                            variant="filled"
                                                        />
                                                    )}
                                                    ListboxProps={{
                                                        sx: { fontSize: "13px" },
                                                    }}
                                                />
                                                {touched?.recipient && errors?.recipient && (
                                                    <FormHelperText
                                                        sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                    >{`${errors?.recipient}`}</FormHelperText>
                                                )}
                                            </Grid2>

                                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                                <FormControl
                                                    component="fieldset"
                                                    fullWidth
                                                    error={touched?.status && Boolean(errors?.status)}
                                                >
                                                    <FormLabel component="legend"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            '&.Mui-focused': {
                                                                color: 'text.secondary', // prevent turning blue on focus
                                                            },
                                                            '&.Mui-error': {
                                                                color: 'text.secondary', // prevent turning red on error
                                                            },
                                                        }}
                                                    >
                                                        Status (Active/Inactive)
                                                    </FormLabel>

                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={Boolean(values?.status)}
                                                                onChange={(e) => handleChange({
                                                                    target: {
                                                                        name: 'status',
                                                                        value: e.target.checked,
                                                                    }
                                                                })}
                                                                onBlur={() => setTouched({ ...touched, status: true })}
                                                                name="status"
                                                                color="primary"
                                                            />
                                                        }
                                                        label={values?.status ? "Active" : "Inactive"}
                                                    />
                                                    {touched?.status && errors?.status && (
                                                        <FormHelperText
                                                            sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                        >{`${errors?.status}`}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, sm: 12 }}>

                                                <Button
                                                    type="submit"
                                                    //loading={isLoading}
                                                    variant="contained"
                                                    sx={{
                                                        marginRight: "20px",
                                                        backgroundColor: `${newtheme?.customSettings?.themeColor}`,
                                                        color: "#FFF",
                                                        padding: "6px 20px",
                                                        fontWeight: "500",
                                                        textTransform: "none",
                                                        float: "right",
                                                        fontSize: "16px",
                                                    }}
                                                >
                                                    Save
                                                </Button>
                                            </Grid2>
                                        </Grid2>
                                    </form>
                                )}
                            </Formik>
                        </Box>
                    </ThemeProvider>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}